import axios from 'axios'
import { 
  ImageData, 
  ImageAnnotation, 
  AnnotationUpdate, 
  PaginatedResponse, 
  AnnotationStats,
  ApiResponse,
  ApproverUpdate,
  SpaceType,
  IssueType
} from '../types'

// API Base URL Configuration
// Priority: Environment variable (VITE_API_BASE_URL) > Auto-detect based on hostname > Local development
const apiBaseUrl = import.meta.env?.VITE_API_BASE_URL

let API_BASE_URL: string

if (typeof apiBaseUrl === 'string' && apiBaseUrl.trim().length > 0) {
  // Use explicit environment variable if provided
  API_BASE_URL = apiBaseUrl.replace(/\/+$/, '')
} else if (typeof window !== 'undefined') {
  // Auto-detect based on current hostname
  const hostname = window.location.hostname
  
  if (hostname === 'app.image-tagger-dev.apps.sabinodb.com') {
    // Development environment
    API_BASE_URL = 'https://api.image-tagger-dev.apps.sabinodb.com'
  } else if (hostname === 'app.image-tagger.apps.sabinodb.com') {
    // Production environment
    API_BASE_URL = 'https://api.image-tagger.apps.sabinodb.com'
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Local development - point to local backend
    API_BASE_URL = 'http://localhost:8000'
  } else {
    // Fallback for unknown environments
    console.warn(`Unknown hostname: ${hostname}, using dev API as fallback`)
    API_BASE_URL = 'https://api.image-tagger-dev.apps.sabinodb.com'
  }
} else {
  // SSR or build-time fallback
  API_BASE_URL = 'https://api.image-tagger-dev.apps.sabinodb.com'
}

// Log the API URL being used for debugging
console.log('🔗 Image Tagger API Base URL:', API_BASE_URL)
console.log('📍 Current hostname:', typeof window !== 'undefined' ? window.location.hostname : 'unknown')

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error)
    return Promise.reject(error)
  }
)

export const imageApi = {
  // Get paginated list of images
  getImages: async (
    page: number = 1,
    limit: number = 50,
    search?: string,
    statusFilter?: string,
    projectFilter?: string,
    space: SpaceType = 'annotator',
    approverStatusFilter?: string
  ): Promise<PaginatedResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      space: space
    })

    if (search) {
      params.append('search', search)
    }

    if (space === 'annotator') {
      if (statusFilter && statusFilter !== 'all') {
        params.append('status_filter', statusFilter)
      }
    } else if (space === 'approver') {
      if (approverStatusFilter && approverStatusFilter !== 'all') {
        params.append('approver_status_filter', approverStatusFilter)
      }
    }

    if (projectFilter && projectFilter !== 'all') {
      params.append('project_filter', projectFilter)
    }

    const response = await api.get(`api/images?${params}`)
    console.log('API Response:', response)
    console.log('API Response Data:', response.data)
    console.log('API Response Data Keys:', Object.keys(response.data || {}))
    console.log('API Response Data.data type:', typeof response.data?.data, Array.isArray(response.data?.data))
    return response.data
  },

  // Get specific image by ID
  getImage: async (imageId: string): Promise<ImageData> => {
    const response = await api.get(`api/images/${encodeURIComponent(imageId)}`)
    return response.data
  },

  // Create or update annotation
  createAnnotation: async (annotation: ImageAnnotation): Promise<ApiResponse> => {
    const response = await api.post('api/annotations', annotation)
    return response.data
  },

  // Update existing annotation
  updateAnnotation: async (imageId: string, update: AnnotationUpdate): Promise<ApiResponse> => {
    const response = await api.put(`api/annotations/${encodeURIComponent(imageId)}`, update)
    return response.data
  },

  // Delete annotation
  deleteAnnotation: async (imageId: string): Promise<ApiResponse> => {
    const response = await api.delete(`api/annotations/${encodeURIComponent(imageId)}`)
    return response.data
  },

  // Get annotation statistics
  getStats: async (): Promise<AnnotationStats> => {
    const response = await api.get('api/stats')
    return response.data
  },

  // Get available projects
  getProjects: async (space: SpaceType = 'annotator'): Promise<string[]> => {
    const params = new URLSearchParams({
      space: space
    })
    const response = await api.get(`api/projects?${params}`)
    return response.data.projects
  },

  // Export annotations as CSV
  exportAnnotations: async (search?: string, statusFilter?: string, projectFilter?: string, space?: string, approverStatusFilter?: string): Promise<Blob> => {
    const params = new URLSearchParams({
      format: 'csv',
    })
    
    if (search) {
      params.append('search', search)
    }
    
    if (space) {
      params.append('space', space)
    }
    
    if (space === 'approver') {
      if (approverStatusFilter && approverStatusFilter !== 'all') {
        params.append('approver_status_filter', approverStatusFilter)
      }
    } else {
      if (statusFilter && statusFilter !== 'all') {
        params.append('status_filter', statusFilter)
      }
    }
    
    if (projectFilter && projectFilter !== 'all') {
      params.append('project_filter', projectFilter)
    }
    
    const response = await api.get(`api/annotations/export?${params}`, {
      responseType: 'blob',
    })
    
    return response.data
  },

  // Download images as ZIP
  downloadImages: async (search?: string, statusFilter?: string, projectFilter?: string, space?: string, approverStatusFilter?: string): Promise<Blob> => {
    const params = new URLSearchParams()
    
    if (search) {
      params.append('search', search)
    }
    
    if (space) {
      params.append('space', space)
    }
    
    if (space === 'approver') {
      if (approverStatusFilter && approverStatusFilter !== 'all') {
        params.append('approver_status_filter', approverStatusFilter)
      }
    } else {
      if (statusFilter && statusFilter !== 'all') {
        params.append('status_filter', statusFilter)
      }
    }
    
    if (projectFilter && projectFilter !== 'all') {
      params.append('project_filter', projectFilter)
    }
    
    const response = await api.get(`api/images/download?${params}`, {
      responseType: 'blob',
    })
    
    return response.data
  },

  // Complete annotations (mark as seen)
  completeAnnotations: async (search?: string, statusFilter?: string, projectFilter?: string): Promise<ApiResponse & { count: number }> => {
    const params = new URLSearchParams()
    
    if (search) {
      params.append('search', search)
    }
    
    if (statusFilter && statusFilter !== 'all') {
      params.append('status_filter', statusFilter)
    }
    
    if (projectFilter && projectFilter !== 'all') {
      params.append('project_filter', projectFilter)
    }
    
    const response = await api.post(`api/annotations/complete?${params}`)
    return response.data
  },

  // Update approver status
  updateApproverStatus: async (approverUpdate: ApproverUpdate): Promise<ApiResponse> => {
    const response = await api.post('api/approver/update', approverUpdate)
    return response.data
  },

  // Complete approver annotations (mark as seen by approver)
  completeApproverAnnotations: async (search?: string, approverStatusFilter?: string, projectFilter?: string): Promise<ApiResponse & { count: number }> => {
    const params = new URLSearchParams()
    
    if (search) {
      params.append('search', search)
    }
    
    if (approverStatusFilter && approverStatusFilter !== 'all') {
      params.append('approver_status_filter', approverStatusFilter)
    }
    
    if (projectFilter && projectFilter !== 'all') {
      params.append('project_filter', projectFilter)
    }
    
    const response = await api.post(`api/approver/complete?${params}`)
    return response.data
  },

  // Get available issues for annotation
  getAvailableIssues: async (): Promise<{ issues: IssueType[] }> => {
    const response = await api.get('api/issues')
    return response.data
  },
}

// Utility function to download blob as file
export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export default api 
